import React from "react";
import styled from "styled-components";
import { FormattedMessage } from "react-intl";
import { submitIfEnter, validateForm } from "../../helpers";

import { Pills } from "./Pills";
import { Input } from "./Input";
import { Select } from "./Select";
import { TextArea } from "./TextArea";
import { FileInput } from "./FileInput";

import { TYPE_SECONDARY, Progress, TYPE_PRIMARY } from "../ui";
import { BTN_MEDIUM, Button, ButtonGroup } from "./Button";
import { ValidationErrors } from "./ValidationErrors";
import { Notification } from "./Notification";
import { i18n, notification, eventEmitter } from "../../services";
import pick from "lodash/pick";
import isUndefined from "lodash/isUndefined";
import isEqual from "lodash.isequal";
import { KEY_ENTER } from "../keyboard/keys";
import { Constraints } from "../../components/Forms/types";

export const FORM_CLEAR = "form.clear";
export const FORM_END_PROCESSING = "form.endProcessing";

export const TEXT_FIELD = "text";
export const PASSWORD_FIELD = "password";
export const EMAIL_FIELD = "email";
export const FILE_FIELD = "file";
export const TEXTAREA_FIELD = "textarea";
export const SELECT_FIELD = "select";
export const PILLS_FIELD = "pills";

const Container = styled.form`
    width: 100%;
    display: flex;
    flex-direction: column;
`;

const Heading = styled.h2`
    margin-bottom: 16px;
    width: 100%;
    text-align: center;

    span {
        font-weight: 500;
    }
`;

const Description = styled.p<{
    dangerouslySetInnerHTML: {
        __html: string;
    };
}>`
    margin-bottom: 20px;
    width: 100%;
    font-size: 17px;
    text-align: center;
`;

interface FormProps {
    editing?: "Edit" | "Create";
    focusOnFirst: boolean;

    updateProp?: string;
    order: string[];

    fields: {
        [k: string]: Record<string, any>;
    };

    constraints: Constraints;

    id: string;
    heading?: string;
    description?: {
        __html: string;
    };

    primaryBtnType?: string;

    [x: string]: any;

    onSubmit: (arg: Record<string, any>) => void;
    onAlternate?: (evt: React.MouseEvent<HTMLElement>) => void;
}
interface FormState {
    processing: boolean;
    showFormErrors: boolean;
    [x: string]: any;
}
export class Form extends React.Component<FormProps, FormState> {
    private initialState: Record<string, string>;

    constructor(props: FormProps) {
        super(props);

        const { order, fields } = props;
        const initialState: Record<string, string> = {};

        // default values, supplied with fields
        order.forEach((k) => {
            initialState[k] = !isUndefined(fields[k]["value"])
                ? fields[k]["value"]
                : "";
        });

        // reducer supplied values
        const { updateProp } = props;
        if (updateProp) {
            const update = props[updateProp];
            if (update) {
                order.forEach((k) => {
                    initialState[k] = !isUndefined(update.get(k))
                        ? update.get(k)
                        : initialState[k];
                });
            }
        }

        this.initialState = initialState;
        this.state = {
            ...initialState,
            ...this.additionalState(),
        };
    }

    additionalState = () => ({
        processing: false,
        showFormErrors: false,
    });

    componentDidMount() {
        eventEmitter.on(FORM_CLEAR, this.onFormClear);
        eventEmitter.on(FORM_END_PROCESSING, this.onFormEndProcessing);
    }

    componentWillUnmount() {
        eventEmitter.off(FORM_CLEAR, this.onFormClear);
        eventEmitter.off(FORM_END_PROCESSING, this.onFormEndProcessing);
    }

    componentWillReceiveProps(nextProps: FormProps) {
        this.setFields(nextProps);
    }

    setFields = (props: FormProps) => {
        if (!props.editing || !props.updateProp) {
            return;
        }

        const nextState: Record<string, any> = {};
        const { order, updateProp } = props;
        const update = props[updateProp];

        if (update) {
            for (const k of order) {
                nextState[k] = !!update.get(k) ? update.get(k) : this.state[k];
            }
        }

        this.initialState = nextState;
        this.setState(nextState);
    };

    onFormEndProcessing = () => this.setState({ processing: false });
    onFormClear = () => {
        this.setState({
            ...this.initialState,
            ...this.additionalState(),
        });
    };

    restrictSubmitNodes = ["textarea"];
    onKeyUp = (
        evt: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const t = evt.target;
        if (!t) {
            return;
        }

        const nodeName = (t as HTMLElement).nodeName.toLowerCase();
        if (
            this.restrictSubmitNodes.includes(nodeName) &&
            evt.key === KEY_ENTER
        ) {
            evt.stopPropagation();
        } else {
            submitIfEnter(evt, this.onSubmit);
        }
    };

    onChange = (key: string, value: any) => {
        notification.clear();
        this.setState({ [key]: value });
    };

    inputTypes = [TEXT_FIELD, PASSWORD_FIELD, EMAIL_FIELD];

    fields = () => {
        const { id, order, fields, focusOnFirst } = this.props;

        return order.map((name, index) => {
            const field = fields[name];
            const { type } = field;
            const fullWidth = field.fullWidth || true;

            const readOnly =
                typeof field.readOnly !== "undefined" ? field.readOnly : false;
            const focusOnLoad = focusOnFirst && index === 0;
            const value =
                typeof this.state[name] !== "undefined"
                    ? this.state[name]
                    : undefined;

            if (this.inputTypes.includes(type)) {
                return (
                    <Input
                        focusOnLoad={focusOnLoad}
                        key={`${name}${id}`}
                        name={name}
                        readOnly={readOnly}
                        type={type}
                        value={value}
                        fullWidth={fullWidth}
                        onKeyUp={this.onKeyUp}
                        onChange={this.onChange}
                        placeholder={i18n.get(`${id}Form.${name}`)}
                    />
                );
            } else if (type === FILE_FIELD) {
                return (
                    <FileInput
                        focusOnLoad={focusOnLoad}
                        key={`${name}${id}`}
                        name={name}
                        accept={field.accept.join(",")}
                        readOnly={readOnly}
                        value={value}
                        fullWidth={fullWidth}
                        onChange={this.onChange}
                        placeholder={i18n.get(`${id}Form.${name}`)}
                    />
                );
            } else if (type === TEXTAREA_FIELD) {
                return (
                    <TextArea
                        key={`${name}${id}`}
                        name={name}
                        readOnly={readOnly}
                        focusOnLoad={focusOnLoad}
                        value={value}
                        fullWidth={fullWidth}
                        onKeyUp={this.onKeyUp}
                        onChange={this.onChange}
                        placeholder={i18n.get(`${id}Form.${name}`)}
                    />
                );
            } else if (type === SELECT_FIELD) {
                const { values, labels, indents } = field;
                return (
                    <Select
                        key={`${name}${id}`}
                        id={id}
                        name={name}
                        values={values}
                        indents={indents}
                        labels={labels}
                        value={value}
                        fullWidth={fullWidth}
                        onChange={this.onChange}
                        placeholder={i18n.get(`${id}Form.${name}`)}
                    />
                );
            } else if (type === PILLS_FIELD) {
                const { values, labels } = field;
                return (
                    <Pills
                        key={`${name}${id}`}
                        id={id}
                        name={name}
                        value={value}
                        values={values as string[]}
                        labels={labels}
                        onChange={this.onChange}
                        placeholder={i18n.get(`${id}Form.${name}`)}
                    />
                );
            }

            return null;
        });
    };

    buttons = () => {
        const { id, primaryBtnType, onAlternate } = this.props;

        return (
            <ButtonGroup>
                <Button
                    type={primaryBtnType || TYPE_PRIMARY}
                    size={BTN_MEDIUM}
                    onClick={this.onSubmit}
                    messageId={`${id}Form.submitBtn${this.appendMsg()}`}
                />

                {onAlternate && (
                    <Button
                        type={TYPE_SECONDARY}
                        size={BTN_MEDIUM}
                        onClick={onAlternate}
                        messageId={`${id}Form.alternateBtn${this.appendMsg()}`}
                    />
                )}
            </ButtonGroup>
        );
    };

    formValidation = () => (
        <ValidationErrors
            show={this.state.showFormErrors}
            errors={validateForm(this.state, this.props.constraints)}
        />
    );

    onSubmit = () => {
        if (this.state.processing) {
            return <Progress />;
        }

        notification.clear();

        const errors = validateForm(this.state, this.props.constraints);
        if (errors.length > 0) {
            return this.setState({ showFormErrors: true });
        }

        const modifiedState = pick(this.state, this.props.order);
        if (isEqual(this.initialState, modifiedState)) {
            return notification.set("form.unmodified", "i");
        }

        this.setState({ processing: true });
        this.props.onSubmit(modifiedState);
    };

    appendMsg() {
        const { editing } = this.props;
        if (typeof editing === "undefined") {
            return "";
        }

        return editing ? "Edit" : "Create";
    }

    render() {
        if (this.state.processing) {
            return <Progress />;
        }

        const { id, heading, description } = this.props;

        return (
            <Container onSubmit={this.onSubmit}>
                <Notification />

                {heading && (
                    <Heading>
                        <FormattedMessage
                            id={`${id}Form.${heading}${this.appendMsg()}`}
                        />
                    </Heading>
                )}

                {description && (
                    <Description dangerouslySetInnerHTML={description} />
                )}

                {this.formValidation()}
                {this.fields()}
                {this.buttons()}
            </Container>
        );
    }
}
